import React, { Fragment, memo, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import ProductAdderHeader from '@src/components/Product/ProductAdderHeader';

import { Grid, ProductAdderArea } from './styles';
import ProductPackageAdderBody from '../ProductPackageAdderBody';
import ProductPackageAdderProductList from '../ProductPackageAdderProductList';
import useBreakpoint from '@src/hooks/useBreakpoint';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { useWidgetId } from '@src/core/IdProvider';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import ProductPackageAdderError from '../ProductPackageAdderError';
import {
  productPackageErrorSelector,
  productPackageProductsSelector,
  productPackageSelector,
  productPackageStatusSelector,
  toggleIsClosingAdderModal,
  closeAdderModal,
  isClosingAdderModalSelector,
  productSelectionStateSelector,
} from '@src/features/product';
import ProductPackageCancelModal from '../ProductPackageCancelModal';
import { track } from '@src/services/analytics';

const ProductPackageAdderRoot = () => {
  const packageId = usePackageIdParam();
  const productId = useSelectedProductId();
  const dispatch = useAppDispatch();

  const widgetId = useWidgetId();

  const productPackage = useAppSelector(productPackageSelector(packageId!));
  const productPackageProducts = useAppSelector(productPackageProductsSelector(packageId!));
  const status = useAppSelector(productPackageStatusSelector(packageId!));
  const error = useAppSelector(productPackageErrorSelector(packageId!));

  const productSelectionState = useAppSelector(productSelectionStateSelector(widgetId));
  const isClosingAdderModal = useAppSelector(isClosingAdderModalSelector(widgetId));

  const { isDesktop, isMobile } = useBreakpoint();

  const shouldRenderProductList = useMemo(() => {
    if (isDesktop) return true;
    if (!isDesktop && !productId) return true;
    if (status === 'completed') return true;
    if (!isDesktop && error) return true;

    return false;
  }, [isDesktop, productId, status, error]);

  const shouldRenderAdderBody = useMemo(() => {
    if (error) return false;
    if (isMobile && status === 'completed') return true;
    if (isDesktop || productId) return true;

    return false;
  }, [isDesktop, productId, isMobile, status, error]);

  const handleCloseAdder = (callback: () => void) => {
    if (packageId && (Object.keys(productSelectionState).length > 0 || productId)) {
      dispatch(toggleIsClosingAdderModal(widgetId, true));
      return;
    }

    callback();
  };

  const handleClose = () => {
    dispatch(toggleIsClosingAdderModal(widgetId, false));
  };

  const handleConfirm = () => {
    track('productPackageCancelConfirm');

    dispatch(closeAdderModal(widgetId, packageId));
  };

  return (
    <Fragment>
      <ProductAdderArea data-cy='product-package-adder-modal'>
        <ProductAdderHeader title={productPackage?.name} onClose={handleCloseAdder} />

        <Grid>
          {!isDesktop ? <ProductPackageAdderError error={error} /> : null}

          {productPackageProducts && productPackageProducts?.length > 0 && (
            <Fragment>
              {shouldRenderProductList ? (
                <ProductPackageAdderProductList productPackageProducts={productPackageProducts} status={status!} />
              ) : null}
            </Fragment>
          )}

          {shouldRenderAdderBody ? <ProductPackageAdderBody /> : null}

          {isDesktop ? <ProductPackageAdderError error={error} /> : null}
        </Grid>
      </ProductAdderArea>

      {isClosingAdderModal ? (
        <ProductPackageCancelModal isOpen={isClosingAdderModal} onConfirm={handleConfirm} onClose={handleClose} />
      ) : null}
    </Fragment>
  );
};

export default memo(ProductPackageAdderRoot);
